import React, { useEffect, useState, memo, useRef } from "react";
import { connect } from "react-redux";
import { TreeView, TreeItem } from '@mui/x-tree-view';
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Drawer,
  Grid,
  Avatar,
  Tooltip,
  Fab,
  IconButton
} from "@mui/material";
import {
  KeyboardArrowRight,
  KeyboardArrowDown,
} from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import { useSidebar } from '../../contexts/SidebarContext';
import { useHistory } from "react-router-dom";

import qodexLogoFull from '../../assets/logos/qodexLogoFull.png';
import qodexDarkLogo from '../../assets/logos/qodexDarkLogo.png';
import "../../styles/components/Dashboard/NavigationDrawer.scss";
import UpgradeDialog from "components/TestCaseGeneratorBot/UpgradeDialog";
import ThemeToggle from "components/Common/ThemeToggle";
import { useTheme } from '../../contexts/ThemeContext';
import { drawerWidth, COLLAPSED_WIDTH } from "config/constants";
import { PanelRightCloseIcon, PanelRightOpen, ChevronDown, ChevronRight, History, MessageSquareText } from "lucide-react";
import AiAgentSidebar from "components/TestCaseGeneratorBot/AiAgentSidebar";

const NavigationDrawer = memo(function NavigationDrawer({ expandedNodeIdsInitial = [], selectedNodeIdInitial = "", drawerItems = [], selectedMenuItem, isItemDisabled, getProjectButton, onProfileClick, profileDropdownRef, selectedProject, user }) {
  const [expandedNodeIds, setExpandedNodeIds] = useState(expandedNodeIdsInitial);
  const [selectedNodeId, setSelectedNodeId] = useState(selectedNodeIdInitial);
  const selectedNodeIdRef = useRef("");
  const [upgradeDialogVisible, setUpgradeDialogVisible] = useState(false);
  const { isDarkMode } = useTheme();
  const { isSidebarCollapsed: isCollapsed, toggleSidebar } = useSidebar();
  const [isRecentsVisible, setIsRecentsVisible] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (selectedNodeIdRef.current !== selectedMenuItem) {
      let selectedChildItem;
      for (let i = 0; i < drawerItems.length; i++) {
        for (let j = 0; j < drawerItems[i].length; j++) {
          const menuItem = drawerItems[i][j];
          selectedChildItem = menuItem?.items?.filter((childItem) => childItem?.id === selectedMenuItem)?.[0];
          if (selectedChildItem) {
            selectedNodeIdRef.current = menuItem?.id;
            setSelectedNodeId(selectedChildItem?.id);
            setExpandedNodeIds([menuItem?.id]);
            break;
          }
        }
        if (selectedChildItem)
          break;
      }
    }
  }, [selectedMenuItem]);

  const renderTopNav = () => {
    return (
      <div className="px-2">
        <List className="topNav">
          <ListItem key="logoNav-1" className="listItem logo" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <img src={isDarkMode ? qodexDarkLogo : qodexLogoFull} alt="Qodex" className="h-12" height="50px" />
            <div className="flex items-center gap-2">
              <IconButton onClick={toggleSidebar}>
                {isCollapsed ? <MenuIcon /> : <PanelRightOpen size={18} color="var(--text-primary)" />}
              </IconButton>
              {/* <ThemeToggle /> */}
            </div>
          </ListItem>
          <ListItem key="logoNav-2" className="listItem">
            {/* {getProjectButton} */}
          </ListItem>
        </List>
      </div>
    )
  }

  const formatNumber = (num) => {
    if (num >= 1_000_000_000_000) {
      return { value: +(num / 1_000_000_000_000).toFixed(1), unit: 'T' }; // Trillion
    }
    if (num >= 1_000_000_000) {
      return { value: +(num / 1_000_000_000).toFixed(1), unit: 'B' }; // Billion
    }
    if (num >= 1_000_000) {
      return { value: +(num / 1_000_000).toFixed(1), unit: 'M' }; // Million
    }
    if (num >= 1_000) {
      return { value: +(num / 1_000).toFixed(1), unit: 'K' }; // Thousand
    }
    return { value: num, unit: '' };
  };

  const renderBottomNav = () => {
    const totalCredits = selectedProject?.total_credits;
    const availableTokens = selectedProject?.available_tokens;
    let userName = user?.name || user?.username || "Qodex User";
    return (
      <div className="bottomNavContainer">
        <Divider />
        <List className="bottomNav">
          <ListItem key="creditInfo" className="listItem creditListItem">
            <div className="creditInfo cursor-pointer flex items-center justify-center">
              <Tooltip title={availableTokens} placement="top">
                <>
                  <span>AI Tokens:</span> <span className={`${availableTokens > 10000 ? "text-[var(--primary-color)]" : "text-red-600"} mx-1`}>
                    {formatNumber(availableTokens).value}
                    {formatNumber(availableTokens).unit}
                  </span>
                </>
              </Tooltip>
              <span>•</span>
              <button
                className='flex items-center border-none text-capitalize !font-medium !rounded-md bg-transparent'
                onClick={() => setUpgradeDialogVisible(true)}
              >
                <span className='text-blue-500'>Upgrade</span>
              </button>
            </div>
          </ListItem>
          <ListItem key="profileNav" className="listItem">
            <ListItemButton onClick={onProfileClick} className="listItemButton">
              <ListItemIcon className="listItemIcon" ref={profileDropdownRef}>
                <Grid container flexDirection="row" justifyContent="center" alignItems="center">
                  <Grid item>
                    <Avatar
                      alt="Qodex User"
                      // src={avatar}
                      sx={{ width: 44, height: 44 }}
                    >
                      {userName.split(' ')?.[0]?.[0]?.toUpperCase()}{userName.split(' ')?.[1]?.[0]?.toUpperCase()}
                    </Avatar>
                  </Grid>
                  <Grid item className="welcome">
                    <div className="greeting">Welcome back 👋</div>
                    <div className="name truncate-1-lines">{userName}</div>
                  </Grid>
                  <Grid item className="action">
                    <ListItemIcon className="listItemIcon">
                      <KeyboardArrowRight />
                    </ListItemIcon>
                  </Grid>
                </Grid>
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        </List>
        <UpgradeDialog
          visible={upgradeDialogVisible}
          onClose={() => setUpgradeDialogVisible(false)}
          availableTokens={availableTokens}
        />
      </div>
    )
  }

  const handleDropdownClick = () => {

  }

  const handleSelectChat = (chatId) => {
    window.location = `/ai-agent?chatId=${chatId}`;
  }

  const handleListItemClick = (item) => {
    selectedNodeIdRef.current = item?.id;
    setSelectedNodeId(item?.id);
    if (item.onClick) {
      item.onClick();
    }
  }

  const handleTreeViewNodeToggle = (event, nodeIds) => {
    setExpandedNodeIds([...nodeIds]);
  }

  const renderNavDropdown = (menuItem) => {
    return (
      <TreeView
        aria-label={`${menuItem?.label} tree`}
        defaultExpandIcon={menuItem.icon}
        defaultCollapseIcon={menuItem.icon}
        defaultExpanded={expandedNodeIds}
        sx={{
          flexGrow: 1,
          maxWidth: 400,
          bgcolor: 'transparent',
          '& .Mui-expanded': {
            bgcolor: 'var(--background-secondary)',
          }
        }}
        expanded={expandedNodeIds}
        selected={selectedNodeId}
        onNodeToggle={handleTreeViewNodeToggle}
        className="dropdownItem py-0 my-1 !ml-0 px-0"
        key={menuItem.id}
      >
        <TreeItem
          key={menuItem.id}
          nodeId={menuItem.id}
          onClick={() => handleDropdownClick({ menuItem })}
          className="treeItemButton my-1 !ml-0"
          sx={{ padding: "0px" }}
          label={(<div>
            {menuItem.label}
            {expandedNodeIds.indexOf(menuItem.id) !== -1 ? <KeyboardArrowDown sx={{ position: "absolute", right: "0px" }} /> : <KeyboardArrowRight sx={{ position: "absolute", right: "0px" }} />}
          </div>)}
        >
          {menuItem?.items?.map((item, index) => {
            const selected = item.id === selectedMenuItem;
            return (
              <TreeItem
                key={item.id}
                nodeId={item.id}
                onClick={(event) => { event.preventDefault(); event.stopPropagation(); handleListItemClick(item); }}
                className="treeItemButtonLeaf ml-0 px-6"
                label={
                  <ListItem key={item.id} className="listItem py-0 ml-0" sx={{ padding: "0px" }}>
                    <a href={`${window?.location?.origin}${item.href}`}>
                      <ListItemButton disabled={isItemDisabled(item.id)} className={`px-3 py-0 listItemButton${selected ? " selected" : ""}`}>
                        <ListItemIcon className="listItemIcon">
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText sx={{ marginLeft: "11px" }} primary={item.label} />
                      </ListItemButton>
                    </a>
                  </ListItem>
                }
              />
            )
          })}
        </TreeItem>
      </TreeView>
    )
  }

  const renderDrawer = () => {
    return drawerItems.map((menuItemsList, index) => {
      return (
        <div key={index}>
          <List className="list" sx={{ backgroundColor: "var(--background-primary)" }}>
            {
              menuItemsList.map((menuItem, index) => {
                if (menuItem.type === 'dropdown') {
                  return renderNavDropdown(menuItem);
                } else {
                  const selected = menuItem.id === selectedMenuItem;
                  return (
                    <ListItem key={menuItem.id} className="listItem py-0 my-1">
                      <a href={`${window?.location?.origin}${menuItem.href}`}>
                        <ListItemButton onClick={(event) => { event.preventDefault(); menuItem.onClick(); }} disabled={isItemDisabled(menuItem.id)} className={`px-3 py-0 listItemButton${selected ? " selected" : ""}`}>
                          <ListItemIcon className="listItemIcon">
                            {menuItem.icon}
                          </ListItemIcon>
                          <ListItemText sx={{ marginLeft: "4px" }} primary={menuItem.label} />
                        </ListItemButton>
                      </a>
                    </ListItem>
                  )
                }
              })
            }
          </List>
          {index < (drawerItems.length - 1) && <Divider key={`divider-${index}`} />}
        </div>
      )
    })
  };

  const renderRecentsButton = () => (
    <button
      type="button"
      className="mt-2 w-full flex items-center justify-between bg-transparent border-none !text-[var(--text-secondary)] hover:text-[var(--text-primary)] px-3 py-1.5 truncate transition-all duration-300 transform shadow-none outline-none"
      onClick={() => setIsRecentsVisible((prev) => !prev)}
      aria-expanded={isRecentsVisible}
      aria-controls="old-chats-section"
    >
      <div>
        <MessageSquareText size={18} />
        <span className="fs13 text-[var(--text-secondary)] pl-3">Recent Chats</span>
      </div>
      <span
        className={`transition-transform duration-200 mr-1.5 ${isRecentsVisible ? "rotate-90" : ""}`}
      >
        <ChevronRight size={17.5} />
      </span>
    </button>
  );

  // Render collapsed sidebar strip
  const renderCollapsedSidebar = () => (
    <Box sx={{ display: "flex" }} className="navigationDrawer">
      <Box
        component="nav"
        sx={{
          width: COLLAPSED_WIDTH,
          flexShrink: { sm: 0 },
          position: 'relative',
          transition: 'width 0.3s ease-in-out'
        }}
        aria-label="collapsed navigation menu"
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: COLLAPSED_WIDTH,
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingTop: '16px',
              transition: 'width 0.3s ease-in-out',
            },
          }}
          open
        >
          <IconButton
            onClick={toggleSidebar}
            sx={{
              marginBottom: '16px',
              color: 'var(--text-primary)',
              '&:hover': {
                backgroundColor: 'var(--hover-bg)',
              }
            }}
          >
            <PanelRightCloseIcon size={18} />
          </IconButton>
        </Drawer>
      </Box>
    </Box>
  );

  // Only render sidebar when not collapsed
  return (
    <>
      {isCollapsed ? renderCollapsedSidebar() : (
        <Box sx={{ display: "flex" }} className="navigationDrawer">
          <Box
            component="nav"
            sx={{
              width: drawerWidth,
              flexShrink: { sm: 0 },
              position: 'relative',
              transition: 'width 0.3s ease-in-out'
            }}
            aria-label="navigation menu items"
          >
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: "none", sm: "block" },
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: drawerWidth,
                  transition: 'width 0.3s ease-in-out',
                  overflowX: 'hidden',
                },
              }}
              open
            >
              {renderTopNav()}
              <div style={{ maxHeight: "calc(100vh - 120px)", overflow: "auto", backgroundColor: "var(--background-primary)" }}>
                <button
                  className='flex items-center justify-center py-1 mx-auto w-[95%] border !border-[var(--borderColor)] hover:bg-[var(--hover-bg)] bgSecondary text-capitalize text-[var(--text-primary)] !rounded-md'
                  onClick={async (e) => {
                    e.preventDefault();
                    history.replace('/ai-agent?newChat=true');
                  }}
                >
                  <span className='fs13'>New Chat</span>
                </button>
                {renderDrawer()}
                <hr className="my-0" />
                {renderRecentsButton()}
                {isRecentsVisible && (
                  <div className="w-full">
                    <AiAgentSidebar
                      isVisible={true}
                      onHide={() => { }}
                      onSelectChat={handleSelectChat}
                      selectedProject={selectedProject}
                    />
                  </div>
                )}
              </div>
              {/* {renderBottomNav()} */}
            </Drawer>
          </Box>
        </Box>
      )}
    </>
  );
});

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    selectedProject: state.common.selectedProject,
    user: state.user.current_user
  }
}

export default connect(mapStateToProps)(NavigationDrawer);
